import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { useSite } from "@/context/SiteContext";
import { Loading, ErrorState } from "@/components/site/Primitives";

export default function EnquiryConfirmation() {
  const { reference } = useParams();
  const { state } = useLocation();
  const { site } = useSite();
  const [data, setData] = useState(state || null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = `Enquiry received — ${reference}`;
    if (!state) {
      api.get(`/public/enquiries/${reference}`).then((r) => setData({ ...r.data, submitted_at: r.data.created_at, service: r.data.service_name }))
        .catch(() => setError("We couldn't find that enquiry reference."));
    }
  }, [reference, state]);

  if (error) return <div className="eb-container py-24"><ErrorState message={error} /></div>;
  if (!data) return <div className="eb-container py-24"><Loading rows={1} /></div>;
  const ackFailed = data.notifications && data.notifications.acknowledgement !== "sent";
  return (
    <div className="eb-container py-20 md:py-32 max-w-4xl" data-testid="enquiry-confirmation-page">
      <CheckCircle2 className="text-[#0055FF]" size={40} />
      <p className="eb-label mt-8">Enquiry received</p>
      <h1 className="mt-4 text-4xl sm:text-6xl font-semibold tracking-[-0.04em]">Brief in the <span className="eb-accent">bay.</span></h1>
      <div className="mt-10 grid sm:grid-cols-3 gap-px bg-[#27272A] border border-[#27272A]">
        <div className="bg-[#050505] p-6"><p className="eb-label">Reference</p><p className="mt-2 font-display text-2xl font-bold text-[#00E5FF]" data-testid="confirmation-reference">{data.reference}</p></div>
        <div className="bg-[#050505] p-6"><p className="eb-label">Service</p><p className="mt-2 text-white">{data.service}</p></div>
        <div className="bg-[#050505] p-6"><p className="eb-label">Submitted</p><p className="mt-2 text-white" data-testid="confirmation-timestamp">{fmtDateTime(data.submitted_at)}</p></div>
      </div>
      <p className="mt-8 text-zinc-400 leading-relaxed max-w-2xl">
        Status: <span className="text-white" data-testid="confirmation-status">{data.status}</span>. We'll review your brief and confirm scope, price and timeline before any work begins.
      </p>
      {ackFailed ? (
        <p className="mt-4 text-amber-400 text-sm max-w-2xl" data-testid="confirmation-email-warning">
          Your enquiry is saved, but we couldn't send the acknowledgement email right now. Please note your reference{site?.footer?.contact_email ? ` or write to ${site.footer.contact_email}` : ""}.
        </p>
      ) : data.notifications && <p className="mt-4 text-zinc-500 text-sm">A confirmation has been sent to your email.</p>}
      <Link to="/" className="eb-btn-ghost mt-10" data-testid="confirmation-home-btn">Back to home</Link>
    </div>
  );
}
